import "./CoursesMain.css";
import { useContext, useEffect, useState } from "react";
import axios from "axios";
import { useParams, useSearchParams } from "react-router-dom";
import { useNavigate } from "react-router";
import CoursesSkeleton from "./CoursesSkeleton/CoursesSkeleton";
import AuthContext from "../../contexts/AuthContext";
import showNotification from "../../utils/showNotification";
import { ERROR_HANDLER } from "../../utils/error-handler";
import NoContentMessage from "../../components/NoContentMessage/NoContentMessage";
import NoLessonsGIF from "../../assets/Others/noLessons.gif";
import { MaterialsTable } from "./MaterialsTable/MaterialsTable";
import { Badge, Button, Divider, Stack } from "@mantine/core";
import { IconBooks, IconSchool, IconCalendar, IconClock, IconFileDescription, IconLock, IconCalendarCheck, IconTimeDuration0, IconVideo, IconMail } from "@tabler/icons-react";
import useOnboarding from "../../hooks/useOnboarding";
import { TOUR_STEPS } from "../../utils/tour";
import OnBoardingBtn from "../../components/OnBoardingBtn/OnBoardingBtn";
import moment from "moment";
import { Link } from "react-router-dom";


const groupEventsByYear = (events) => {
   return events.reduce((acc, event) => {
      const date = event.data?.initDate;
      const year = date ? moment(date).format("YYYY") : "Sin Fecha";

      if (!acc[year]) {
         acc[year] = [];
      }
      acc[year].push(event);
      return acc;
   }, {});
};

const getGroupedEvents = (events) => {
   const grouped = groupEventsByYear(events);
   return Object.entries(grouped)
      .map(([year, data]) => ({ year, data }))
      .sort((a, b) => Number(b.year) - Number(a.year));
};

const getEventTypeBadge = (type) => {
   switch (type) {
      case "lessons":
         return { label: "Clase", color: "var(--color-secondary)" };
      case "exams":
         return { label: "Examen", color: "var(--color-tertiary)" };
      case "recoveryExam":
      case "reExam":
         return { label: "Recuperatorio", color: "var(--color-tertiary-dark)" };
      default:
         return { label: "Evento", color: "gray" };
   }
};

const CoursesMain = () => {
   const [searchParams] = useSearchParams();
   const is_newsletter = searchParams.get('newsletter') === 'true';
   const [courseData, setCourseData] = useState(null);
   const [loading, setLoading] = useState({ isLoading: false, error: null });
   const [avals, setAvals] = useState([]);
   const { courseId } = useParams();
   const { authToken } = useContext(AuthContext);
   const [allCourseMaterials, setAllCourseMaterials] = useState([]);
   const [isMaterial, setIsMaterial] = useState(is_newsletter || false);
   const navigate = useNavigate();

   const [runOnboarding, setRunOnboarding] = useState(false);
   const tour = useOnboarding({
      steps: TOUR_STEPS.course,
      page: 'course',
      disabled: loading.isLoading || loading.error,
      runAgain: runOnboarding,
      lastBtnFunction: () => setRunOnboarding(false)
   });

   const fetchCourseData = async () => {
      setLoading((prevState) => ({ ...prevState, isLoading: true }));
      try {
         const { data: courseInfoData } = await axios.get(
            `${process.env.REACT_APP_BACKEND_SERVER}campus/course/${courseId}`,
            {
               headers: {
                  "Content-Type": "application/json",
                  Authorization: "Bearer " + authToken,
               },
            }
         );
         setCourseData(courseInfoData);

         const { data: allCourseMaterialsData } = await axios.get(
            `${process.env.REACT_APP_BACKEND_SERVER}campus/class/${courseId}/allMaterials`,
            {
               headers: {
                  "Content-Type": "application/json",
                  Authorization: "Bearer " + authToken,
               },
            }
         );
         setAllCourseMaterials(allCourseMaterialsData);
      } catch (error) {
         console.error("Error fetching course data:", error);
         setLoading(prevState => ({ ...prevState, error: 'Lo siento, ha ocurrido un error.' }));
         showNotification({
            color: "red",
            status: "error",
            title: "Ha ocurrido un problema traer información sobre el curso.",
            message: `Detalle: ${ERROR_HANDLER[error.request.status] || ERROR_HANDLER.defaultError}`,
            autoClose: 12000
         });
      } finally {
         setLoading((prevState) => ({ ...prevState, isLoading: false }));
      }
   };

   const fetchAvals = async () => {
      try {
         const { data } = await axios.get(
            `${process.env.REACT_APP_BACKEND_SERVER}info/avals`
         );
         setAvals(data);
      } catch (error) {
         console.error("Error fetching avals:", error);
      }
   };

   useEffect(() => {
      fetchCourseData();
      fetchAvals();
   }, [courseId]);

   if (loading.isLoading) {
      return <CoursesSkeleton />;
   }

   const handleEventClick = (event) => {
      if (event.type === "lessons") {
         navigate(`/campus/clase/${event.data._id}`);
      } else if (event.type === "exams" || event.type === "recoveryExam" || event.type === "reExam") {
         const currentDate = moment();
         const examInitDate = moment(event.data.initDate);
         const examEndDate = moment(event.data.endDate);
         const isExamAvailable = currentDate >= examInitDate && currentDate <= examEndDate;

         if (isExamAvailable) {
            navigate(`/campus/antes-de-evaluar/${encodeURIComponent(courseData.name)}/${event.data._id}`);
         } else {
            showNotification({
               color: "yellow",
               status: "warning",
               title: "Parece que aún no es la fecha del examen. Podrás ingresar ese mismo día.",
            });
         }
      }
   };

   const lessonsCount = courseData?.classQuantity || 0;
   const examsCount = courseData?.events?.filter(e => e.type === "exams" || e.type === "recoveryExam" || e.type === "reExam").length || 0;

   const IS_SUBSCRIPTION = courseData?.type?.toUpperCase() === 'SUSCRIPCION'
   const renderTitles = (is_subscription) => {
      if (is_subscription) return {
         material: "Newsletters",
         class: "Workshops",
      }
      return {
         material: "Materiales",
         class: "Clases"
      }
   }

   const renderButtonText = (is_subscription, isMaterial) => {
      if (is_subscription) return <>
         {isMaterial
            ? <>
               <span className="coursesMain-material--buttonText">Ver workshops</span>
               <IconVideo size={24} />
            </>
            : <>
               <span className="coursesMain-material--buttonText">Ver newsletters</span>
               <IconMail size={24} />
            </>}
      </>


      return <>{isMaterial
         ? <>
            <span className="coursesMain-material--buttonText">Ver clases</span>
            <IconSchool size={24} />
         </>
         : <>
            <span className="coursesMain-material--buttonText">Ver material</span>
            <IconBooks size={24} />
         </>}</>
   }

   if (courseData && !loading.error && !loading.isLoading) {
      return (
         <>
            <OnBoardingBtn onClick={() => setRunOnboarding(true)} />
            <div className="coursesMain-wrapper">
               {tour}
               {/* Header Section */}
               <section className="coursesMain-header">
               
                  <div className="coursesMain-header__top">
                     <div className="coursesMain-header__info">
                        <h1 className="coursesMain-header__title">{courseData.name}</h1>

                        {!IS_SUBSCRIPTION && <div className="coursesMain-header__badges">
                           {lessonsCount > 0 && (
                              <Badge
                                 variant="outline"
                                 color="var(--color-text-secondary)"
                                 size="lg"
                                 leftSection={<IconSchool size={14} />}
                              >
                                 {lessonsCount} {lessonsCount === 1 ? "clase" : "clases"}
                              </Badge>
                           )}
                           {examsCount > 0 && (
                              <Badge
                                 variant="outline"
                                 color="var(--color-text-secondary)"
                                 size="lg"
                                 leftSection={<IconFileDescription size={14} />}
                              >
                                 {examsCount} {examsCount === 1 ? "examen" : "exámenes"}
                              </Badge>
                           )}
                           <Badge
                              variant="outline"
                              color="var(--color-text-secondary)"
                              size="lg"
                              leftSection={<IconCalendar size={14} />}
                           >
                              {courseData?.classDays}
                           </Badge>
                           <Badge
                              variant="outline"
                              color="var(--color-text-secondary)"
                              size="lg"
                              leftSection={<IconClock size={14} />}
                           >
                              {courseData?.classHour}
                           </Badge>
                        </div>}

                        {courseData.description && (
                           <p className="coursesMain-header__description">{courseData.description}</p>
                        )}
                     </div>
                     <div className="coursesMain-header__image-container">
                        <img src={courseData.image} className="coursesMain-header__image" alt={courseData.name} />
                     </div>
                  </div>

                  {avals.length > 0 && (
                     <div className="coursesMain-avals">
                        <h3 className="coursesMain-avals__title">{IS_SUBSCRIPTION ? "Nuestros avales" : "Avalan esta capacitación"}</h3>
                        <div className="coursesMain-avals__list">
                           {avals.map((aval) => (
                              <Link
                                 key={aval._id}
                                 to={aval.web}
                                 target="_blank"
                                 className="coursesMain-avals__item"
                              >
                                 <img
                                    src={aval.image}
                                    alt={aval.name}
                                    className="coursesMain-avals__image"
                                 />
                              </Link>
                           ))}
                        </div>
                     </div>
                  )}
               </section>

               {/* Section Header + Toggle Button */}
               <section className="coursesMain-section-header">
                  <h2 className="coursesMain-section-header__title">
                     {isMaterial ? renderTitles(IS_SUBSCRIPTION).material : renderTitles(IS_SUBSCRIPTION).class}
                  </h2>
                  <Button
                     id="CoursesMain-toggle-button"
                     onClick={() => setIsMaterial(!isMaterial)}
                     color='var(--color-tertiary)'
                  >
                     {renderButtonText(IS_SUBSCRIPTION, isMaterial)}
                  </Button>
               </section>

               {/* Content: Materials or Timeline */}
               {isMaterial
                  ? <section className="coursesMain-material-table-container">
                     <MaterialsTable data={allCourseMaterials.general} height='max-content'
                        tableTitle={IS_SUBSCRIPTION ? "Todos los newsletters" : "Materiales generales"} />
                     {!IS_SUBSCRIPTION && <MaterialsTable data={allCourseMaterials.lessons} height='max-content'
                        tableTitle='Materiales por clase' />}
                  </section>
                  : <section className="coursesMain-timeline">
                     {courseData?.events?.length > 0 ? (
                        (() => {
                           const groupedEvents = getGroupedEvents(courseData.events);
                           const hasMultipleYears = groupedEvents.length > 1;
                           return groupedEvents.map(({ year, data }, groupIdx) => {
                              let groupIndex = 0;

                              return (
                                 <div key={year} className="coursesMain-timeline__group">
                                    {(hasMultipleYears && groupIdx !== 0) && <Divider w='100%' my={20} />}
                                    {hasMultipleYears && (
                                       <h2 className="coursesMain-timeline__year">{year}</h2>
                                    )}
                                    <div className="coursesMain-timeline__list">
                                       {data.map((event) => {
                                          groupIndex++;
                                          const badge = getEventTypeBadge(event.type);
                                          const isExam = event.type !== "lessons";
                                          const currentDate = moment();
                                          const examAvailable = isExam &&
                                             currentDate >= moment(event.data.initDate) &&
                                             currentDate <= moment(event.data.endDate);

                                          return (
                                             <div
                                                key={event.data._id}
                                                className={`coursesMain-timeline__item ${isExam ? 'coursesMain-timeline__item--exam' : ''}`}
                                                onClick={() => handleEventClick(event)}
                                             >
                                                <div className="coursesMain-timeline__marker">
                                                   <span
                                                      className="coursesMain-timeline__number"
                                                      style={{ backgroundColor: badge.color }}
                                                   >
                                                      {groupIndex}
                                                   </span>
                                                   <div className="coursesMain-timeline__line" />
                                                </div>
                                                <div className="coursesMain-timeline__content">
                                                   <div className="coursesMain-timeline__content-header">
                                                      <Badge size="xs" color={badge.color}>
                                                         {badge.label}
                                                      </Badge>
                                                      {event.data.initDate && (
                                                         <span className="coursesMain-timeline__date">
                                                            <IconCalendar size={13} />
                                                            {moment(event.data.initDate).format("DD/MM/YYYY")}
                                                         </span>
                                                      )}
                                                   </div>
                                                   <div className="coursesMain-timeline__content-body">
                                                      <p className="coursesMain-timeline__name">
                                                         {event.data.name}
                                                      </p>
                                                      <Button
                                                         size="xs"
                                                         fw="normal"
                                                         variant='outline'
                                                         color={isExam ? "var(--color-tertiary-dark)" : "var(--color-secondary-dark)"}
                                                         disabled={isExam && !examAvailable}
                                                         onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleEventClick(event);
                                                         }}
                                                         className="coursesMain-timeline__btn"
                                                      >
                                                         {isExam ? "Ir" : IS_SUBSCRIPTION ? "Acceder" : "Ver clase"}
                                                      </Button>
                                                   </div>
                                                   {isExam && !examAvailable && (
                                                      <span className="coursesMain-timeline__locked">
                                                         <IconLock size={13} />
                                                         Disponible el {moment(event.data.initDate).format("DD/MM/YYYY")}
                                                      </span>
                                                   )}
                                                </div>
                                             </div>
                                          );
                                       })}
                                    </div>
                                 </div>
                              );
                           });
                        })()
                     ) : (
                        <NoContentMessage
                           gif={NoLessonsGIF}
                           title="De momento no hay clases disponibles."
                           message="¡Nuestro equipo está trabajando para que accedas a tu contenido lo más pronto posible!"
                        />
                     )}
                  </section>}
            </div>
         </>
      );
   }

   return (
      <p className="coursesMain-errorText">
         Lo sentimos, hubo un error al cargar la página. Intenta mas tarde.
      </p>
   );
};

export default CoursesMain;
